import { useState, FC, useEffect } from 'react';

import { AirportSearchViewModel } from '@api/index';
import AirportAutoComplate, {
  AirPortTypes,
} from '@domain/flight/searchBar/airportAutoComplate';
import { CompareArrow, FlightLandFill, FlightTakeoffFill } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import useSearchBar from '@contexts/searchbar';
import { FlightSearchBarActionType } from '@contexts/searchbar/types/flight';
import ResponsiveAirportSearch from '@components/views/flight/listing/SearchBar/AirportSearch/ResponsiveAirportSearch';
import { useBreakpoint } from '@contexts/breakpoints';

import { StyledButton, Wrapper, StyledContent, WrapperMobile } from './styled';

type AirportDataTypes = {
  departure?: AirPortTypes;
  arrival?: AirPortTypes;
  history?: AirportSearchViewModel[];
  clearPastFlightSearch?: () => void;
  removePastFlightSearch?: (item: AirportSearchViewModel) => void;
  isListingPage?: boolean;
};

const AirportSelection: FC<AirportDataTypes> = ({
  history,
  clearPastFlightSearch,
  removePastFlightSearch,
  isListingPage,
}) => {
  const { xl } = useBreakpoint();
  const {
    state: { flight: state },
    dispatch,
  } = useSearchBar();

  const [openAutoCompleteResult, setOpenAutoCompleteResult] =
    useState<boolean>(false);
  const [isShowSearchListingLocation, setIsSHowSearchListingLocation] =
    useState(false);

  const [selectedDeparture, setSelectedDeparture] = useState(
    state.departureAirport
  );
  const [selectedArrival, setSelectedArrival] = useState(state.arrivalAirport);

  const [data, setData] = useState<AirportDataTypes>({
    departure: state.departureAirport || null,
    arrival: state.arrivalAirport || null,
  });

  useEffect(() => {
    setSelectedDeparture(state.departureAirport);
  }, [state.departureAirport]);

  useEffect(() => {
    setSelectedArrival(state.arrivalAirport);
  }, [state.arrivalAirport]);

  const handleChange = (type: string, value: AirPortTypes) => {
    setData((p) => ({ ...p, [type]: value }));
    if (type === 'departure') {
      dispatch({
        type: FlightSearchBarActionType.SET_DEPARTURE_AIRPORT,
        payload: value || null,
      });
    } else if (type === 'arrival') {
      dispatch({
        type: FlightSearchBarActionType.SET_ARRIVAL_AIRPORT,
        payload: value || null,
      });
    }
  };

  const handleChangeValues = () => {
    const [departure, arrival] = [
      data.departure || state.departureAirport,
      data.arrival || state.arrivalAirport,
    ];

    if (arrival && departure) {
      handleChange('departure', arrival);
      setSelectedDeparture(arrival);

      handleChange('arrival', departure);
      setSelectedArrival(departure);
    }
  };

  const applySearchDepartureLocationHandler = (
    currentSelectedLocation: AirportSearchViewModel
  ) => {
    if (!currentSelectedLocation?.airportName) {
      dispatch({
        type: FlightSearchBarActionType.SET_IS_DEPARTURE_ERROR,
        payload: true,
      });
      return;
    }

    if (state.isDepartureError) {
      dispatch({
        type: FlightSearchBarActionType.SET_IS_DEPARTURE_ERROR,
        payload: false,
      });
    }
    setSelectedDeparture(currentSelectedLocation);
    handleChange('departure', currentSelectedLocation);

    setIsSHowSearchListingLocation(false);
  };

  const applySearchArrivalLocationHandler = (
    currentSelectedLocation: AirportSearchViewModel
  ) => {
    if (!currentSelectedLocation?.airportName) {
      dispatch({
        type: FlightSearchBarActionType.SET_IS_ARRIVAL_ERROR,
        payload: true,
      });
      return;
    }

    if (state.isArrivalError) {
      dispatch({
        type: FlightSearchBarActionType.SET_IS_ARRIVAL_ERROR,
        payload: false,
      });
    }
    setSelectedArrival(currentSelectedLocation);
    handleChange('arrival', currentSelectedLocation);

    setIsSHowSearchListingLocation(false);
  };

  /**
   * Handling Clicking on close location search icon
   */
  const handleCloseLocationSearch = () => {
    setIsSHowSearchListingLocation(false);
    setOpenAutoCompleteResult(false);
  };

  /**
   * Handling Clicking on the location search icon
   */
  const handleOpenLocationSearch = () => {
    setIsSHowSearchListingLocation(true);
  };

  const { xl: isDesktop } = useBreakpoint();

  return (
    <>
      {isDesktop ? (
        <StyledContent data-testid='airport-selection-container'>
          <Wrapper isListingPage={isListingPage}>
            <AirportAutoComplate
              label='Nereden'
              onChange={(airport) => handleChange('departure', airport)}
              icon={FlightTakeoffFill}
              value={state.departureAirport}
              history={history}
              clearPastFlightSearch={clearPastFlightSearch}
              removePastFlightSearch={removePastFlightSearch}
              isLocationError={state.isDepartureError}
              setIsLocationError={(value) =>
                dispatch({
                  type: FlightSearchBarActionType.SET_IS_DEPARTURE_ERROR,
                  payload: value,
                })
              }
            />
          </Wrapper>
          <StyledButton onClick={handleChangeValues}>
            <CustomIcon
              icon={CompareArrow}
              fontSize={xl ? 24 : 13}
              paletteColor='grey800'
            />
          </StyledButton>

          <Wrapper isListingPage={isListingPage}>
            <AirportAutoComplate
              label='Nereye'
              onChange={(airport) => handleChange('arrival', airport)}
              icon={FlightLandFill}
              value={state.arrivalAirport}
              history={history}
              clearPastFlightSearch={clearPastFlightSearch}
              removePastFlightSearch={removePastFlightSearch}
              isLocationError={state.isArrivalError}
              setIsLocationError={(value) =>
                dispatch({
                  type: FlightSearchBarActionType.SET_IS_ARRIVAL_ERROR,
                  payload: value,
                })
              }
            />
          </Wrapper>
        </StyledContent>
      ) : (
        <StyledContent>
          <WrapperMobile>
            <ResponsiveAirportSearch
              onAirportSelect={(airport) => handleChange('departure', airport)}
              title='Nereden Gideceksiniz?'
              placeholder='Nereden'
              drawerTitle='Gidiş Uçuşu'
              icon={FlightTakeoffFill}
              pastSearchesData={history}
              onRemoveItem={removePastFlightSearch}
              onClearClick={clearPastFlightSearch}
              openAutoCompleteResultModal={openAutoCompleteResult}
              setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
              handleCloseLocationSearch={handleCloseLocationSearch}
              showLocationModal={handleOpenLocationSearch}
              open={isShowSearchListingLocation}
              handleUpdateButton={applySearchDepartureLocationHandler}
              locationAirport={selectedDeparture}
              subTitle={selectedDeparture?.airportName}
              error={state.isDepartureError}
            />
            <StyledButton onClick={handleChangeValues}>
              <CustomIcon
                icon={CompareArrow}
                fontSize={xl ? 24 : 13}
                paletteColor='grey800'
              />
            </StyledButton>
            <ResponsiveAirportSearch
              onAirportSelect={(airport) => handleChange('arrival', airport)}
              title='Nereye Gideceksiniz?'
              placeholder='Nereye'
              drawerTitle='Dönüş Uçuşu'
              icon={FlightLandFill}
              pastSearchesData={history}
              onRemoveItem={removePastFlightSearch}
              onClearClick={clearPastFlightSearch}
              openAutoCompleteResultModal={openAutoCompleteResult}
              setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
              handleCloseLocationSearch={handleCloseLocationSearch}
              showLocationModal={handleOpenLocationSearch}
              open={isShowSearchListingLocation}
              handleUpdateButton={applySearchArrivalLocationHandler}
              locationAirport={selectedArrival}
              subTitle={selectedArrival?.airportName}
              error={state.isArrivalError}
            />
          </WrapperMobile>
        </StyledContent>
      )}
    </>
  );
};
export default AirportSelection;
